/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// spice/lamps.js — the LEDs, diodes and resistive elements of a desk as the
// voltage solve sees them (features/done/spice-lite-leds.md). Pure and DOM-free.
//
// The digital engine asks only whether an LED's legs are strongly driven (a
// supply or an output on both sides burns it; a resistor anywhere saves it).
// Spice Lite asks what a bench would: how many milliamps flow. Every LED,
// display segment, diode and Zener is a JUNCTION branch of the one network
// solve (spice/voltages.js, through spice/network.js), and every resistor,
// pot side and rnet9 element a resistive one — this file reads them off the
// desk once per netlist (`lampTopology`), with the HOLE each lead is in:
//
//   · an LED or a segment (spice/leds.js: nothing below its knee, then its
//     dynamic resistance — each colour its own datasheet's), and a character
//     LCD's backlight (its colour's LED behind the board's resistor);
//   · a diode or a Zener (spice/diodes.js: the one common silicon junction,
//     a Zener also conducting backwards past its voltage);
//   · a resistive element, its ohms (a pot's side its own share).
//
// What the solve then says of each junction — its current, the voltage
// across it — is its verdict (lit, how bright, overdriven, reversed, and
// BURNT past its maximum junction temperature: opened, and the solve run
// again without it, until nothing more burns — spice/engine.js), and the
// current through every lead is what the probe reads out.
//
// Also here: what a HIGH from a bench SOURCE is (`sourceVolts`) — a clock
// brick or a signal flag, an ideal source the solve holds a net at.

import { partDef } from "../../catalog/index.js";
import { partPinAddresses } from "../../model/occupancy.js";
import { boardNodeOf, parseAddress } from "../../model/breadboard.js";
import { backlightSpec, ledSpec } from "./leds.js";
import { diodeSpec } from "./diodes.js";
import { resistorKey } from "./supply.js";

// The Newton machinery is the shared one; the solver's own entry points stay
// importable from here, where they began.
export { gaussSolve } from "./network.js";

/** Each netlist's lamp topology (`lampTopology`), for the reason
    spice/supply.js caches its own: a netlist lives exactly as long as the
    facts below. */
const TOPOLOGY = new WeakMap();

/**
 * What a HIGH from a BENCH SOURCE is, volts, by the net it drives — an ideal
 * source that draws nothing (Jason, 2026-10-07):
 *   · a clock brick: the supply on its own `vcc` terminal (an unpowered one
 *     drives no net, and is not here);
 *   · a signal flag, or a serial Input tag: the LOWEST supply among the chips
 *     reading that net, the one level every one of them can take (a flag on
 *     a 5 V circuit is a 5 V source on a desk that also holds 12 V) — null
 *     when no chip reads it, for the caller's own fallback (spice/voltages.js:
 *     the lowest supply on the desk, by the same rule).
 * @param {object} ctx - sim/engine.js's context
 * @returns {Map<string, number|null>}
 */
export function sourceVolts(ctx) {
  const out = new Map();
  for (const clk of ctx.clocks ?? []) {
    if (clk.outNet) out.set(clk.outNet, clk.volts ?? null);
  }
  const signalNets = new Set((ctx.signals ?? []).map((sig) => sig.net).filter(Boolean)); // prettier-ignore
  if (!signalNets.size) return out;
  const lowest = new Map(); // net → the lowest reader's supply
  for (const c of ctx.chips ?? []) {
    const volts = ctx.chipStatus?.get(c.comp.id)?.volts ?? c.supplyVolts;
    if (!(volts > 0)) continue;
    for (const p of c.def.pins) {
      if (p.role !== "input" && p.role !== "io") continue;
      const net = c.pinNet.get(p.n);
      if (!signalNets.has(net)) continue;
      lowest.set(net, Math.min(lowest.get(net) ?? Infinity, volts));
    }
  }
  for (const net of signalNets) {
    if (!out.has(net)) out.set(net, lowest.get(net) ?? null);
  }
  return out;
}

/** The key one junction is reported under: an LED's component id, or a
    display's id and segment (`c4#a`). */
export function junctionKey(compId, segId = null) {
  return segId == null ? compId : `${compId}#${segId}`;
}

/**
 * A diode part's junctions, each `[key, [anodePin, cathodePin]]`: a diode's
 * one under its component id, as an LED's; a part of several (a bridge
 * rectifier's four) each under `<id>#d<n>`, n from 1 in `oneWayBridges`
 * order. The one statement of those keys — the overlay reads the burn back
 * by them.
 * @param {object} def - a def with `oneWayBridges`
 * @param {{id: string, params?: object}} comp
 * @returns {Array<[string, [number, number]]>}
 */
export function diodeKeys(def, comp) {
  const pairs = def.oneWayBridges(comp.params);
  if (pairs.length === 1) return [[junctionKey(comp.id), pairs[0]]];
  return pairs.map((pair, i) => [junctionKey(comp.id, `d${i + 1}`), pair]);
}

/** The segment part of a junction key (`d2` of `c4#d2`), or null. */
function keySegment(key) {
  const at = key.indexOf("#");
  return at < 0 ? null : key.slice(at + 1);
}

/**
 * Every LED junction and every resistive element on a desk, by net — what a
 * run cannot change. Computed once per netlist.
 * @param {object} doc
 * @param {{netOfPoint: Map}} netlist
 */
export function lampTopology(doc, netlist) {
  const cached = TOPOLOGY.get(netlist);
  if (cached) return cached;
  const netOf = (address) =>
    address ? (netlist.netOfPoint.get(address) ?? null) : null;
  const wiredBy = nodeUsers(doc);
  const junctions = []; // {key, comp, seg, spec, anode, cathode, anodeAt, cathodeAt}
  const resistors = []; // {key, a, b, aAt, bAt, ohms}
  const outputsAt = new Map(); // chip id → pin → the hole its lead is in
  for (const comp of doc.components ?? []) {
    const def = partDef(comp.ref);
    if (!def || comp.board == null) continue;
    const led = typeof def.polarity === "function" && Boolean(def.colors);
    const segments = Array.isArray(def.segments) ? def.segments : null;
    const resistive = typeof def.weakBridges === "function";
    const drives = Boolean(def.logic) && def.pins?.some((p) => p.role === "output" || p.role === "io"); // prettier-ignore
    const diode = Boolean(def.diode || def.rectifier) && typeof def.oneWayBridges === "function"; // prettier-ignore
    const backlight = def.backlight ?? null;
    if (!led && !segments && !diode && !resistive && !drives && !backlight) continue; // prettier-ignore
    const pins = partPinAddresses(doc, comp);
    if (!pins) continue;
    const at = new Map(pins.map((p) => [p.pin, p.address]));
    if (drives) outputsAt.set(comp.id, at);
    const junction = (
      key,
      seg,
      anodePin,
      cathodePin,
      isDiode = false,
      spec = null,
    ) => {
      // prettier-ignore
      const anodeAt = at.get(anodePin) ?? null;
      const cathodeAt = at.get(cathodePin) ?? null;
      junctions.push({
        key,
        comp: comp.id,
        seg,
        diode: isDiode,
        spec: spec ?? (isDiode ? diodeSpec(def, comp.params) : ledSpec(comp.params?.color)), // prettier-ignore
        anode: netOf(anodeAt),
        cathode: netOf(cathodeAt),
        anodeAt,
        cathodeAt,
      });
    };
    if (diode) {
      // A diode or Zener (spice/diodes.js): its one-way pair is anode →
      // cathode. A bridge rectifier's four pairs are four junctions, each
      // keyed by its place in the list (`c4#d1` … `c4#d4` — diodeKeys).
      for (const [key, [anodePin, cathodePin]] of diodeKeys(def, comp)) {
        junction(key, keySegment(key), anodePin, cathodePin, true);
      }
    } else if (led) {
      const { anodePin, cathodePin } = def.polarity(comp.params);
      junction(junctionKey(comp.id), null, anodePin, cathodePin);
    } else if (segments) {
      const lead = commonLeads(def, comp, at, wiredBy);
      for (const seg of segments) {
        junction(junctionKey(comp.id, seg.id), seg.id, lead(seg.anodePin), lead(seg.cathodePin)); // prettier-ignore
      }
    }
    if (backlight) {
      // A character LCD's backlight: its colour's LED behind the board's
      // resistor (catalog/parts.js LCD_BACKLIGHT).
      const { anodePin, cathodePin, ohms } = backlight;
      junction(junctionKey(comp.id, "backlight"), "backlight", anodePin, cathodePin, false, backlightSpec(comp.params?.color, ohms)); // prettier-ignore
    }
    if (resistive) {
      for (const [pa, pb, own] of def.weakBridges(comp.params)) {
        const ohms = Number(own ?? comp.params?.ohms);
        const aAt = at.get(pa) ?? null;
        const bAt = at.get(pb) ?? null;
        const a = netOf(aAt);
        const b = netOf(bAt);
        if (!(ohms > 0) || !a || !b || a === b) continue;
        resistors.push({ key: resistorKey(aAt, bAt), a, b, aAt, bAt, ohms });
      }
    }
  }
  const topo = { junctions, resistors, outputsAt };
  TOPOLOGY.set(netlist, topo);
  return topo;
}

/**
 * Who has a lead in each board node — every wire end and every seated part's
 * pin, by `board.node` — read once, when a display first asks (`commonLeads`).
 * @returns {(address: string, self: string) => boolean} whether anything but
 *   part `self` is in `address`'s node
 */
function nodeUsers(doc) {
  let users = null;
  const boards = new Map((doc.boards ?? []).map((b) => [b.id, b]));
  const nodeKey = (address) => {
    const { boardId, hole } = parseAddress(address) ?? {};
    const board = boards.get(boardId);
    if (!board) return address; // a brick's terminal is a node of its own
    return `${boardId}.${boardNodeOf(board, hole)}`;
  };
  const add = (address, owner) => {
    if (!address) return;
    const key = nodeKey(address);
    if (!users.has(key)) users.set(key, new Set());
    users.get(key).add(owner);
  };
  return (address, self) => {
    if (!users) {
      users = new Map();
      for (const w of doc.wires ?? []) {
        add(w.from, w.id);
        add(w.to, w.id);
      }
      for (const c of doc.components ?? []) {
        if (c.board == null) continue;
        for (const p of partPinAddresses(doc, c) ?? []) add(p.address, c.id);
      }
    }
    const here = users.get(nodeKey(address));
    return Boolean(here && [...here].some((owner) => owner !== self));
  };
}

/**
 * A display's bridged commons (`internalBridges`, the 7-segment's pins 3
 * and 8) are one strip inside it, and its segments name the first. The
 * current they carry leaves by whichever the BUILD wired: a display grounded
 * through pin 8 alone booked every segment's milliamps on pin 3's hole, with
 * nothing plugged into it. Each common answers for its group when its own
 * node holds nothing else and another's does; wired both ways (or neither),
 * it keeps its own pin.
 * @returns {(pin: number) => number}
 */
function commonLeads(def, comp, at, wiredBy) {
  if (typeof def.internalBridges !== "function") return (pin) => pin;
  const swap = new Map();
  for (const group of def.internalBridges(comp.params) ?? []) {
    const wired = group.filter((pin) => {
      const address = at.get(pin);
      return address != null && wiredBy(address, comp.id);
    });
    if (!wired.length) continue;
    for (const pin of group) {
      if (!wired.includes(pin)) swap.set(pin, wired[0]);
    }
  }
  return (pin) => swap.get(pin) ?? pin;
}
